import {
    SpaceBetween,
    BreadcrumbGroup,
    Flashbar,
    Header,
    Container,
    Form,
    FormField,
    Input,
    Button,
    ColumnLayout,
} from '@amzn/awsui-components-console';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import ConfigFormField from './ConfigFormField';
import * as Constants from '../constants';
import * as GlobalConstants from '../../../lib/constants';
import { isValidInput } from '../../../lib/utils';
import { configureGroup, configureldap } from '../apis';
import { RegionContext } from '../../../CustomContext';

/**
 * Configure group component
 * @param {*} props
 * @returns
 */
export default function ConfigureGroup(props) {
    const { id } = useParams();
    const history = useHistory();
    const { state } = props.location;
    const [group, setGroup] = React.useState({});
    const [showConfigure, setShowConfigure] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [ldap, setLDAP] = React.useState('');
    const [ldapDisabled, setLDAPDisabled] = React.useState(false);
    const [ldapError, setLDAPError] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [widgetChecked, setWidgetChecked] = React.useState(false);
    const [widgetSelectedOptions, setWidgetSelectedOptions] = React.useState([]);
    const [widgetSelectionDisabled, setWidgetSelectionDisabled] = React.useState(false);
    const [teardownChecked, setTeardownChecked] = React.useState(false);
    const [teardownSelectedOptions, setTeardownSelectedOptions] = React.useState([]);
    const [teardownSelectionDisabled, setTeardownSelectionDisabled] = React.useState(false);
    const [setupChecked, setSetupChecked] = React.useState(false);
    const [setupSelectedOptions, setSetupSelectedOptions] = React.useState([]);
    const [setupSelectionDisabled, setSetupSelectionDisabled] = React.useState(false);
    const { selectedRegion, setSelectedRegion } = useContext(RegionContext);

    /**
     * if config not provided, default to be true
     * Otherwise, populate the list by selected/added item(s)
     * @param {*} config
     * @param {*} setDefaultChecked
     * @param {*} options
     * @param {*} setSelectedOptions
     * @param {*} setSelectionDisabled
     */
    const setConfig = (config, setDefaultChecked, options, setSelectedOptions, setSelectionDisabled) => {
        let selectedOptions = [];
        if (!config) {
            setDefaultChecked(true);
            setSelectionDisabled(true);
        } else {
            options.forEach((item) => {
                if (config.includes(item.label)) {
                    selectedOptions.push(item);
                }
            });
            setSelectedOptions(selectedOptions);
        }
    };

    /**
     * get selected item(s) label and populate a list
     * @param {*} selectedOptions
     * @returns
     */
    const getConfig = (selectedOptions) => {
        return selectedOptions.map((option) => option.label);
    };

    /**
     * display error message
     * @param {*} header
     * @param {*} content
     */
    const displayErrorContent = (header, content) => {
        setErrorMessage([
            {
                header: header,
                type: 'error',
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () => setErrorMessage([]),
                content: <>{content}</>,
                action: (
                    <Button href={GlobalConstants.CONTACT_US} iconAlign="right" target="_blank">
                        Contact us
                    </Button>
                ),
            },
        ]);
    };

    /**
     * click confirm button to trigger event
     */
    const onClick = async () => {
        let success = false;
        setLoading(true);
        setErrorMessage([]);
        setLDAPError('');
        try {
            if (!ldapDisabled && !isValidInput(ldap)) {
                setLDAPError('LDAP group is required and should not be empty.');
                success = false;
            } else {
                if (!ldapDisabled) {
                    await configureldap(id, ldap, selectedRegion.value);
                }
                let config = {};
                if (!widgetChecked) {
                    config[Constants.GROUP_CONFIG_KEYS.widgets] = getConfig(widgetSelectedOptions);
                }
                if (!teardownChecked) {
                    config[Constants.GROUP_CONFIG_KEYS.teardown] = getConfig(teardownSelectedOptions);
                }
                if (!setupChecked) {
                    config[Constants.GROUP_CONFIG_KEYS.setup] = getConfig(setupSelectedOptions);
                }
                await configureGroup(id, config, selectedRegion.value);
                success = true;
            }
        } catch (err) {
            const errResponse = err.response;
            if (errResponse && errResponse.data && errResponse.status == 400) {
                const errorException = errResponse.data.errorException || 'ClientError';
                const errorMessage = errResponse.data.errorMessage || errResponse.data;
                displayErrorContent(errorException, errorMessage);
            } else {
                displayErrorContent(
                    'InternalServerError',
                    'Cannot configure this group, try again later or contact our dev team @ds2-device-farm-dev for more details.'
                );
            }
        }
        setLoading(false);
        if (success) {
            history.push('/lab-management/groups/');
        }
    };

    useEffect(() => {
        if (!id || !state) {
            setShowConfigure(false);
            history.push('/lab-management/groups/');
        } else {
            setGroup(state);
            setLDAP(state['ldap'] || '');
            setLDAPDisabled(state['thirdParty'] === 'Yes' || false);
            setShowConfigure(true);

            const config = state['config'];
            if (config) {
                setConfig(
                    config[Constants.GROUP_CONFIG_KEYS.widgets],
                    setWidgetChecked,
                    Constants.WIDGETS_OPTIONS,
                    setWidgetSelectedOptions,
                    setWidgetSelectionDisabled
                );
                setConfig(
                    config[Constants.GROUP_CONFIG_KEYS.teardown],
                    setTeardownChecked,
                    Constants.TEARDOWN_OPTIONS,
                    setTeardownSelectedOptions,
                    setTeardownSelectionDisabled
                );
                setConfig(
                    config[Constants.GROUP_CONFIG_KEYS.setup],
                    setSetupChecked,
                    Constants.SETUP_OPTIONS,
                    setSetupSelectedOptions,
                    setSetupSelectionDisabled
                );
            }
        }
    }, []);
    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Group management', href: '#/lab-management/groups' },
                    { text: 'Configure', href: '#/lab-management/groups/configure' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            {showConfigure && (
                <form onSubmit={(e) => e.preventDefault()}>
                    <Form
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button formAction="none" href="#/lab-management/groups">
                                    Back
                                </Button>
                                <Button formAction="none" loading={loading} onClick={onClick} variant="primary">
                                    Confirm
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        <SpaceBetween direction="vertical" size="m">
                            <Container header={<Header variant="h2">{id}</Header>}>
                                <SpaceBetween direction="vertical" size="m">
                                    <ColumnLayout borders="horizontal" columns={1}>
                                        <FormField label="Group name">
                                            <Input value={group['name'] || ''} disabled />
                                        </FormField>
                                        <FormField
                                            label="Associated LDAP"
                                            description="LDAP group that is associated with this DDF group. Third party group is disabled with ldap association."
                                            errorText={ldapError}
                                        >
                                            <Input
                                                placeholder="Enter a valid ldap group"
                                                value={ldap}
                                                onChange={(event) => {
                                                    setLDAP(event.detail.value);
                                                    setLDAPError('');
                                                }}
                                                disabled={ldapDisabled}
                                            />
                                        </FormField>
                                        <ConfigFormField
                                            label="Widget"
                                            description={Constants.GROUP_CONFIG_DESCRIPTION.widgets}
                                            checked={widgetChecked}
                                            setChecked={setWidgetChecked}
                                            options={Constants.WIDGETS_OPTIONS}
                                            selectedOptions={widgetSelectedOptions}
                                            setSelectedOptions={setWidgetSelectedOptions}
                                            selectionDisabled={widgetSelectionDisabled}
                                            setSelectionDisabled={setWidgetSelectionDisabled}
                                        />
                                        <ConfigFormField
                                            label="Teardown"
                                            description={Constants.GROUP_CONFIG_DESCRIPTION.teardown}
                                            checked={teardownChecked}
                                            setChecked={setTeardownChecked}
                                            options={Constants.TEARDOWN_OPTIONS}
                                            selectedOptions={teardownSelectedOptions}
                                            setSelectedOptions={setTeardownSelectedOptions}
                                            selectionDisabled={teardownSelectionDisabled}
                                            setSelectionDisabled={setTeardownSelectionDisabled}
                                        />
                                        <ConfigFormField
                                            label="Setup"
                                            description={Constants.GROUP_CONFIG_DESCRIPTION.setup}
                                            checked={setupChecked}
                                            setChecked={setSetupChecked}
                                            options={Constants.SETUP_OPTIONS}
                                            selectedOptions={setupSelectedOptions}
                                            setSelectedOptions={setSetupSelectedOptions}
                                            selectionDisabled={setupSelectionDisabled}
                                            setSelectionDisabled={setSetupSelectionDisabled}
                                        />
                                    </ColumnLayout>
                                </SpaceBetween>
                            </Container>
                            <Flashbar items={errorMessage} />
                        </SpaceBetween>
                    </Form>
                </form>
            )}
        </SpaceBetween>
    );
}
